import { Cents } from "nate-react-api-helpers";
import { SubAPI } from "./SubAPI";
import {Paginated} from "./Users";
import {DateString} from "./Projects";
import {GlassSpec, QuotePriceRider} from "./QuotePricing";
import {Preps} from "./Products";

export type PriceLine2Category = "door-hm" | "frame-hm" | "door-wd" | "hardware" | "div10" | "frame-anchor" | "glazing"

export class Pricing extends SubAPI {
    list(props: {
        project: number;
        offset?: number;
        category?: PriceLine2Category;
        sov?: number;
        forSovLink?: boolean;
        previewChangeset?: number;
        filterByChangeset?: boolean;
        sidebarForChangeset?: number;
        opening?: number;
        refunded?: boolean;
        awarded?: boolean;
    }) {
        return this.fetcher.get<Paginated<PriceLine2>>("/api/pricing", props);
    }

    getZerosByCategory(props: {
        project: number;
        previewChangeset?: number;
        filterByChangeset?: boolean;
        refunds?: boolean;
        sov?: number;
    }) {
        return this.fetcher.get<ZeroItem[]>("/api/pricing/zeros-by-category", props);
    }

    upsert(input: PriceLine2) {
        return this.fetcher.post<PriceLine2>("/api/pricing/item", input)
    }

    listRiders(props: {
        project: number;
        offset?: number;
        sov?: number;
        forSovLink?: boolean;
        awarded?: boolean;
    }) {
        return this.fetcher.get<Paginated<PriceRider>>("/api/price-rider", props);
    }

    getProjectMarkups(props: {
        project: number;
        alternative?: number;
    }) {
        return this.fetcher.get<MarkupResult>("/api/pricing/project-markups", props);
    }

    updateProjectMarkups(props: {
        project: number;
        alternative?: number;
        "door-hm"?: number;
        "door-wd"?: number;
        "door-other"?: number;
        hardware?: number;
        frame?: number;
        glazing?: number;
        "div10-wr-accessory"?: number;
        "div10-wr-partition"?: number;
        "div10-locker"?: number;
        "div10-mailbox"?: number;
        "div10-corner-guard"?: number;
        updateExisting: boolean;
    }) {
        return this.fetcher.post("/api/pricing/project-markups", props);
    }

    getCostInOtherQuotes(props: {
        product: number;
        project: number;
        itemName?: string;
    }) {
        return this.fetcher.get<SimilarCost[]>("/api/pricing/cost-in-other-quotes", props);
    }

    priceLookup(props: {
        product: number;
    }) {
        return this.fetcher.get<PriceLookupResult[]>("/api/pricing/price-lookup", props)
    }

    scaleSection(props: {
        project: number;
        ids: number[]
        totalPriceCents: number;
    }) {
        return this.fetcher.post("/api/pricing/section-total-price", props);
    }
}

export type PriceLookupResult = {
    unitCostCents: number;
}

export type PriceCategory = "frame-hm" |
    "door-wd" |
    "door-hm" |
    "hardware" |
    "frame-anchor" |
    "div10" |
    "glazing"

export type ZeroItem = {
    category: PriceCategory,
    count: number;
}

export type MarkupResult = {
    "door-hm": number;
    "door-wd": number;
    "door-other": number;
    hardware: number;
    frame: number
    glazing: number;
    "div10-wr-accessory": number;
    "div10-wr-partition": number;
    "div10-locker": number;
    "div10-mailbox": number;
    "div10-corner-guard": number;
}

export type SimilarCost = {
    project: number;
    projectName: string;
    projectNumber: number;
    avgUnitCostCents: number;
    avgMarkupPercent: number | null;
    date: DateString;
}

export type PriceRider = QuotePriceRider & {
    backup?: PriceRider;
    sovHeading: number | null;
    sovHeadingName: string | null;
};

export type PriceLine2 = {
    id: number;
    project: number;
    archived: boolean;
    inventoryRequest: number;
    productType: string;
    qty: number;
    unitCostCents: Cents;
    extendedPrice: Cents;
    extendedPriceCentsAwarded: Cents | null;
    markupCents?: Cents | null;
    markupPercent?: number | null;

    sovHeading: number | null;
    sovHeadingName: string | null;

    partialPriceFromChangeset: number | null;
    refunded: boolean;
    refundKeepPercent: number;
    refundFullRefund: boolean;
    refundAmount: number;

    inventoryStatus: string;

    name: string;
    hardwareGroup?: number;
    type: string;
    keyed: boolean;

    openingName: string;
    opening: number;
    floor: string;
    locationOne: string;
    locationTransition: string;
    locationTwo: string;
    exterior: boolean;
    manufacturer: number;
    manufacturerName: string;
    product: number;
    productCode: string;
    productName: string;
    finish: string;
    dimensions: string;
    dimWidth: string;
    dimHeight: string;
    dimLength: string;

    keyingType?: string;

    productCategory?: number;
    productCategoryName?: string;
    categoryName?: string;

    room?: string;
    category?: string;
    note?: string;

    revenueDelivered: Cents;
    costDelivered: Cents;

    div10: Div10Spec;
    door?: Door;
    frame?: Frame;
    glass?: GlassSpec;

    quoteProjectPrice?: number;

    backup?: PriceLine2;

    updatedAt: DateString;
    updatedBy: number;

    removedInChangesetFlag?: number | null;
    updatedInChangesetFlag?: number | null;
}

export type PriceLine = {
    id: number;
    baseProjectPrice: number|null;

    itemName: string | null;
    doorSeries: string | null;
    frameSeries: string | null;
    openingSeqNumber: number|null;
    project: number;
    productType: string;
    product: number;
    productName: string;
    categoryName: string;
    categorySortOrder?: number;
    productCode: string;
    finish: string;
    productDimensions: string;
    dimWidth: string;
    dimHeight: string;
    dimLength: string;
    qty: number;
    unitCostCents: Cents;
    markupPercent?: number;
    markupCents?: Cents;
    extendedPrice: Cents;
    revenueDelivered?: Cents;
    costDelivered?: Cents;
    createdAt: DateString;
    updatedAt: DateString;
    updatedBy: number;
    archived: boolean;

    manufacturer: number;
    manufacturerName: string;
    
    supplier: number;
    supplierName: string;

    linkedQuotePrice: number[]
    frame: Frame;
    door: Door;
    div10?: Div10Spec;
    glass?: GlassSpec;

    projectChangeset?: number;
    projectChangesetNote?: string;
    projectChangesetActive?: boolean;
    projectChangesetApproved?: boolean;
}

export type Div10Category =
    'corner-guard' | 'locker' | 'mailbox' | 'wr-accessory' | 'wr-partition';

export type Div10Spec = {
    id: number;

    category: Div10Category;
    productCode: string;

    partitionBracing: string;
    partitionCoating: string;
    description: string; // pulled from div10 record, not in the spec
    note: string;

    lockerTiers: string;
    lockerSloped: boolean;
    lockerEndPanels: boolean;
    lockerBaseTrim: boolean;
    lockerCoating: string;

    mailboxLoading: string;
    mailboxMounting: string;
    mailboxTenantUnits: number;
    mailboxParcelUnits: number;

    cornerGuardFinish: string;
    cornerGuardType: string;
}

export type Frame = {
    id: number;
    series:        string;
    material:      string;
    gauge:         string;
    screenElev:    string;
    screenElevFile?: number;
    // set when screenElevFile resolves to a drw rendering (frame is drw-drawn): the rendering id
    // and one unarchived drw-linked quote opening to open the drw editor against.
    screenElevRendering?: number;
    screenElevQuoteOpening?: number;
    profile:       string;
    jambDepth:     string;
    doorThickness:     string;
    construction:  string;
    handing:       string;
    preps: Preps;
    label:         string;
    type:          string;
    width: string;
    height: string;
}

export type Door = {
    id:            string
    series:        string
    material:      string
    finish:      string
    gauge:         string
    elevation:          string
    elevationFile?: number;
    // set when elevationFile resolves to a drw rendering (door is drw-drawn): the rendering id
    // and one unarchived drw-linked quote opening to open the drw editor against.
    elevationRendering?: number;
    elevationQuoteOpening?: number;
    core:          string
    seam:          string
    handing:       string
    preps: string
    label: string
    width: string;
    height: string;
    thickness: string;
}